import { useMemo } from "react";

import { FavoriteLead } from "features/leads/components";

import BulkManageDesktop from "common/components/BulkManageDesktop";
import { DataTable } from "common/components/DataTable";
import useDataTable from "common/components/DataTable/useDataTable";
import ProfileImage from "common/components/ui/ProfileImage";
import Skeleton from "common/components/ui/Skeleton";
import { typedObjectEntries } from "common/helpers/utils";
import type { Lead } from "common/types";

import { LEADS_COLUMNS, MANAGE_LEADS_ACTIONS } from "../../../constants";
import LeadNotesIndicator from "../LeadNotesIndicator";
import AddNotes from "../ManageLeads/AddNotes";
import FavoriteLeads from "../ManageLeads/FavoriteLeads";
import UpdateLabels from "../ManageLeads/UpdateLabels";

import type { ColumnDef, VisibilityState } from "@tanstack/react-table";

interface DesktopTableProps {
  leads: Lead[];
  selectedLeads: Lead[];
  setSelectedLeads: (selectedLeads: Lead[]) => void;
  openSidebar: (leadId: string, action: "preview" | "notes") => void;
  isLoading: boolean;
  pageSize: number;
  totalItemCount: number;
  columnVisibility: VisibilityState;
  setColumnVisibility: (columnVisibility: VisibilityState) => void;
}

export default function DesktopTable({
  leads,
  selectedLeads,
  setSelectedLeads,
  openSidebar,
  isLoading,
  pageSize,
  totalItemCount,
  columnVisibility,
  setColumnVisibility,
}: DesktopTableProps) {
  const columns = useMemo<ColumnDef<Lead>[]>(() => {
    return [
      {
        id: "account",
        cell: ({ row: { original } }) => {
          const { id, picture_url, favorite, full_name, notes } = original;
          return (
            <div className="flex gap-x-2.5 items-center flex-1 overflow-hidden">
              {isLoading ? (
                <Skeleton className="rounded-full size-8" />
              ) : (
                <ProfileImage src={picture_url} size="sm" />
              )}

              {isLoading ? (
                <Skeleton className="w-28 h-4" />
              ) : (
                <>
                  <span
                    className="text-start text-xs-bold text-nowrap truncate flex-1"
                    title={full_name}
                  >
                    {full_name}
                  </span>
                  <div className="flex gap-x-2.5 ml-1 items-center">
                    <LeadNotesIndicator
                      noteCount={notes.length}
                      onClick={() => openSidebar(id, "notes")}
                    />
                    <FavoriteLead leadId={id} isFavorite={favorite} />
                  </div>
                </>
              )}
            </div>
          );
        },
        enableHiding: false,
      },
      ...typedObjectEntries(LEADS_COLUMNS).map(
        ([id, column]): ColumnDef<Lead> => {
          const { label, icon, renderTag } = column;

          let className = "min-w-40 max-w-40";
          let loaderClassName = "h-4 w-32";

          if (id === "accounts") {
            loaderClassName = "h-5 w-20 rounded-full";
          } else if (id === "labels") {
            className = "min-w-56 max-w-56";
            loaderClassName = "h-6 w-20";
          }

          return {
            id,
            header: label,
            cell: ({ row: { original } }) =>
              isLoading ? (
                <Skeleton className={loaderClassName} />
              ) : (
                renderTag(original, true)
              ),
            meta: { icon, className },
          };
        },
      ),
    ];
  }, [isLoading]);

  const table = useDataTable(
    {
      data: leads || new Array(pageSize).fill({}),
      columns,
    },
    {
      onRowSelectionChange: setSelectedLeads,
      selectedRows: selectedLeads,
      columnVisibility,
      onColumnVisibilityChange: setColumnVisibility,
    },
  );

  return (
    <DataTable
      title="Leads"
      table={table}
      onRowClick={({ id }) => openSidebar(id, "preview")}
      totalItemCount={totalItemCount}
      isLoading={isLoading}
      selectionActionsComponent={
        <BulkManageDesktop
          actions={MANAGE_LEADS_ACTIONS}
          selection={selectedLeads}
          clearSelection={() => setSelectedLeads([])}
          renderActionComponent={({ action, onClose }) => {
            const commonProps = {
              selectedLeads,
              onClose,
            };
            if (action === "labels") {
              return <UpdateLabels {...commonProps} />;
            }
            if (action === "notes") {
              return <AddNotes {...commonProps} />;
            }
            return <FavoriteLeads action={action} {...commonProps} />;
          }}
        />
      }
    />
  );
}
